/**
 * FleetPolicyPanel — "Fleetové podmínky" decision-support panel.
 *
 * Shows the active importer discount programs applicable to a vehicle/offer
 * (`GET /v1/fleet-policy/match`), ranked best-match first. Matching is
 * best-effort — vehicle model year and powertrain are often unknown — so rows
 * that visibly fail a criterion are dimmed and badged, never hidden, and
 * nothing applies automatically. With `onApplyPercent`/`onApplyKc` set (the
 * offer wizard's discount step), each value renders as a click-to-prefill
 * button honouring the sleva semantics (% vs Kč bez DPH).
 */

import { Icon } from '@/components/Icon';
import { toast } from '@/components/Toaster';
import { formatInt, formatMoney } from '@/lib/format';
import {
  FLEET_POLICY_TYP_LABEL,
  openFleetPolicyZdroj,
  useFleetPolicyMatchQuery,
  type FleetPolicyMatchResponse,
  type FleetPolicyMatchRow,
  type FleetPolicyVerdict,
} from './api';

interface FleetPolicyPanelProps {
  vozidloId?: number;
  nabidkaId?: number;
  /** Prefill a percentage discount (offer wizard). */
  onApplyPercent?: (procento: number) => void;
  /** Prefill an absolute Kč bez DPH discount (offer wizard) — retail UV amounts. */
  onApplyKc?: (kc: number) => void;
  /** Prefill an absolute Kč s DPH discount — retail OV zvýhodnění is quoted s DPH. */
  onApplyKcSDph?: (kc: number) => void;
  /** Render a short note instead of nothing when no program matches. */
  showEmpty?: boolean;
  /** Collapse the whole panel behind a closed-by-default disclosure (detail page). */
  collapsible?: boolean;
}

function VerdictBadge({ verdict, label }: { verdict: FleetPolicyVerdict; label: string }) {
  if (verdict === 'ok') return null;
  const cls =
    verdict === 'fail'
      ? 'border-danger/40 text-danger'
      : 'border-line text-ink-3';
  const text = verdict === 'fail' ? `${label} mimo` : `${label}?`;
  const title =
    verdict === 'fail'
      ? `Podle údajů vozidla řádek nesplňuje podmínku (${label}).`
      : `${label} vozidla není znám — ověřte podmínku ručně.`;
  return (
    <span
      title={title}
      className={`inline-block border px-1 py-px font-mono text-[9.5px] uppercase tracking-[0.06em] ${cls}`}
    >
      {text}
    </span>
  );
}

function rowFails(m: FleetPolicyMatchRow): boolean {
  return m.mr === 'fail' || m.pohon === 'fail';
}

function ValueChip({
  label,
  value,
  unit,
  onApply,
}: {
  label: string | null;
  value: number;
  unit: '%' | 'Kč';
  onApply?: () => void;
}) {
  const text = `${label ? `${label} ` : ''}${unit === '%' ? `${formatInt(value)} %` : `${formatMoney(value)} Kč`}`;
  if (!onApply) {
    return (
      <span className="inline-block border border-line bg-bg px-1.5 py-0.5 font-mono text-[11px] tabular-nums text-ink">
        {text}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={onApply}
      title="Předvyplnit slevu"
      className="inline-flex items-center gap-1 border border-line bg-bg px-1.5 py-0.5 font-mono text-[11px] tabular-nums text-ink transition-colors hover:border-accent hover:text-accent"
    >
      {text}
      <Icon name="arrowRight" size={10} />
    </button>
  );
}

export function FleetPolicyPanel({
  vozidloId,
  nabidkaId,
  onApplyPercent,
  onApplyKc,
  onApplyKcSDph,
  showEmpty = false,
  collapsible = false,
}: FleetPolicyPanelProps) {
  const match = useFleetPolicyMatchQuery({ vozidlo: vozidloId, nabidka: nabidkaId });

  if (match.isLoading || match.isError) return null;
  const data = match.data;
  // Defensive on shape — an unexpected/empty payload must never crash the
  // detail page hosting the panel.
  const programy = data?.programy ?? [];
  if (!data || programy.length === 0) {
    if (!showEmpty) return null;
    return (
      <p className="text-[12px] text-ink-3">
        Žádná platná fleet policy pro toto vozidlo — programy se spravují v{' '}
        <span className="font-mono text-[11px]">Vozidla → Správa → Fleet policy</span>.
      </p>
    );
  }

  const headerText = data.vozidlo ? (
    <>
      Fleetové podmínky — {data.vozidlo.modelNazev}
      {data.vozidlo.modelovyRok != null && ` · MR ${data.vozidlo.modelovyRok}`}
      {data.vozidlo.pohon && ` · ${data.vozidlo.pohon}`}
    </>
  ) : null;

  // Detail page: collapse the whole panel (fleet + retail tables) behind a
  // closed-by-default disclosure — it's tall and purely informational there.
  if (collapsible) {
    return (
      <details data-testid="fleet-policy-panel" className="group border border-line bg-paper">
        <summary className="flex cursor-pointer select-none list-none items-center gap-2 px-3 py-2 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-2 transition-colors hover:text-accent">
          <span className="text-ink-3 group-open:rotate-90 transition-transform">▸</span>
          {headerText ?? 'Fleetové podmínky'}
          <span className="ml-1 normal-case tracking-normal text-ink-3">
            ({programy.length} {programy.length === 1 ? 'program' : 'programy'})
          </span>
        </summary>
        <div className="flex flex-col gap-4 border-t border-line p-3">
          <FleetPolicyBody
            programy={programy}
            onApplyPercent={onApplyPercent}
            onApplyKc={onApplyKc}
            onApplyKcSDph={onApplyKcSDph}
          />
        </div>
      </details>
    );
  }

  return (
    <div data-testid="fleet-policy-panel" className="flex flex-col gap-4">
      {headerText && (
        <p className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
          {headerText}
        </p>
      )}
      <FleetPolicyBody
        programy={programy}
        onApplyPercent={onApplyPercent}
        onApplyKc={onApplyKc}
        onApplyKcSDph={onApplyKcSDph}
      />
    </div>
  );
}

/** The program tables + footer note, shared by the flat and collapsible layouts. */
function FleetPolicyBody({
  programy,
  onApplyPercent,
  onApplyKc,
  onApplyKcSDph,
}: {
  programy: FleetPolicyMatchResponse['programy'];
  onApplyPercent?: (procento: number) => void;
  onApplyKc?: (kc: number) => void;
  onApplyKcSDph?: (kc: number) => void;
}) {
  return (
    <>
      {programy.map(({ version, rows }) => (
        <div key={version.id} className="border border-line bg-paper">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 border-b border-line bg-bg px-3 py-2">
            <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-2">
              {FLEET_POLICY_TYP_LABEL[version.program_typ]}
            </span>
            <span className="text-[11px] text-ink-3">
              {version.nazev}
              {version.platnost_do ? ` · do ${version.platnost_do}` : ''}
            </span>
            {version.zdroj_klic && (
              <button
                type="button"
                onClick={() =>
                  openFleetPolicyZdroj(version.id, version.zdroj_nazev).catch((err) =>
                    toast.error(err instanceof Error ? err.message : 'Stažení PDF selhalo'),
                  )
                }
                title={version.zdroj_nazev ?? 'Zdrojové PDF programu'}
                className="ml-auto inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3 transition-colors hover:text-accent"
              >
                <Icon name="download" size={10} />
                Zobrazit PDF
              </button>
            )}
          </div>
          <ul>
            {rows.map((m) => (
              <li
                key={m.radek.id}
                data-testid="fleet-policy-panel-row"
                className={`flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-line px-3 py-2 last:border-b-0 ${
                  rowFails(m) ? 'opacity-45' : ''
                }`}
              >
                <div className="min-w-[140px] flex-1">
                  <span className="text-[12px] font-semibold text-ink">{m.radek.model_nazev}</span>
                  {m.radek.pohon && (
                    <span className="ml-1.5 font-mono text-[10px] text-ink-3">{m.radek.pohon}</span>
                  )}
                  {(m.radek.podminka || m.radek.varianta) && (
                    <div className="text-[11px] leading-snug text-ink-3">
                      {[m.radek.varianta, m.radek.podminka].filter(Boolean).join(' · ')}
                    </div>
                  )}
                </div>
                <span className="flex items-center gap-1">
                  <VerdictBadge verdict={m.mr} label="MR" />
                  <VerdictBadge verdict={m.pohon} label="pohon" />
                </span>
                {/* Data-driven: a fleet version also carries JSDH percentages
                    (procento) and "Ostatní programy" Kč bonuses — every value
                    a row has renders, whatever the program type. */}
                <span className="flex flex-wrap items-center gap-1.5">
                  {m.radek.pasmo_a != null && (
                    <ValueChip label="A" value={m.radek.pasmo_a} unit="%" onApply={onApplyPercent ? () => onApplyPercent(m.radek.pasmo_a!) : undefined} />
                  )}
                  {m.radek.pasmo_b != null && (
                    <ValueChip label="B" value={m.radek.pasmo_b} unit="%" onApply={onApplyPercent ? () => onApplyPercent(m.radek.pasmo_b!) : undefined} />
                  )}
                  {m.radek.pasmo_c != null && (
                    <ValueChip label="C" value={m.radek.pasmo_c} unit="%" onApply={onApplyPercent ? () => onApplyPercent(m.radek.pasmo_c!) : undefined} />
                  )}
                  {m.radek.procento != null && (
                    <ValueChip label={null} value={m.radek.procento} unit="%" onApply={onApplyPercent ? () => onApplyPercent(m.radek.procento!) : undefined} />
                  )}
                  {/* Retail OV quotes the customer zvýhodnění s DPH, UV bez
                      DPH — each prefills the matching wizard field. */}
                  {m.radek.kc_zvyhodneni != null &&
                    (version.program_typ === 'retail_ov' ? (
                      <ValueChip label="Zákazník s DPH" value={m.radek.kc_zvyhodneni} unit="Kč" onApply={onApplyKcSDph ? () => onApplyKcSDph(m.radek.kc_zvyhodneni!) : undefined} />
                    ) : (
                      <ValueChip label="Zákazník bez DPH" value={m.radek.kc_zvyhodneni} unit="Kč" onApply={onApplyKc ? () => onApplyKc(m.radek.kc_zvyhodneni!) : undefined} />
                    ))}
                  {m.radek.kc_fmc != null && (
                    <span className="font-mono text-[10.5px] tabular-nums text-ink-3" title="Příspěvek FMC (podpora dealerovi)">
                      FMC {formatMoney(m.radek.kc_fmc)} Kč
                    </span>
                  )}
                  {m.radek.kc_ap != null && (
                    <span className="font-mono text-[10.5px] tabular-nums text-ink-3" title="Doporučený příspěvek AP">
                      AP {formatMoney(m.radek.kc_ap)} Kč
                    </span>
                  )}
                </span>
                {m.radek.poznamka && (
                  <span className="w-full text-[10.5px] text-ink-3">{m.radek.poznamka}</span>
                )}
              </li>
            ))}
          </ul>
          {/* Program rules (pásma, zvýhodněné skupiny, vyloučení, Ford Credit
              kombinovatelnost) — the human-applied conditions the table can't
              carry. Collapsed by default; the notes are long. */}
          {version.poznamka && (
            <details className="group border-t border-line" data-testid="fleet-policy-panel-notes">
              <summary className="cursor-pointer select-none list-none px-3 py-2 font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3 transition-colors hover:text-accent">
                <span className="group-open:hidden">Podmínky programu ▸</span>
                <span className="hidden group-open:inline">Podmínky programu ▾</span>
              </summary>
              <p className="whitespace-pre-wrap px-3 pb-3 text-[11.5px] leading-relaxed text-ink-2">
                {version.poznamka}
              </p>
            </details>
          )}
        </div>
      ))}
      <p className="text-[10.5px] leading-snug text-ink-3">
        Orientační přehled podle aktivních programů importéra — podmínky (výbava, MR, kombinovatelnost)
        ověřte ve vyhlášení programu. Nic se neaplikuje automaticky.
      </p>
    </>
  );
}
